import { Delete, Help } from '@mui/icons-material';
import {
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';

import { ACCProduct, ACCService } from 'src/@types/cart';
import {
  getCartState,
  updatePrice,
  updateQuantity,
} from 'src/redux/slices/cart';
import { dispatch } from 'src/redux/store';
import { fCurrency } from 'src/utils/formatNumber';
import {
  isDiscreteProductStockUnit,
  minPosSaleQuantity,
  posSaleQuantityStep,
  projectedCartQtyForProductLine,
  roundPosSaleQuantity,
} from 'src/utils/saleLineQuantity';

import { CartTransactionEnum } from '.';
import IncrementerButton from '../custom-input/IncrementerButton';
import { useStrings } from 'src/hooks/useStrings';

type Props = {
  item: ACCProduct | ACCService;
  idx: number;
  transactionType: CartTransactionEnum;
  itemType: 'products' | 'services';
  removeItem: VoidFunction;
  disablePriceField?: boolean;
  originalQtyInSale?: number;
};

export const exceptThisSymbols = ['e', 'E', '+', '-'];

export const PriceModifier = ({
  item,
  transactionType,
  itemType,
  removeItem,
  idx,
  disablePriceField,
  originalQtyInSale,
}: Props) => {
  const { strings } = useStrings();
  const itemAsProduct = item as ACCProduct;
  const { products, services } = useSelector(getCartState);
  const { enqueueSnackbar } = useSnackbar();
  const showInvalidStockMessage = () => {
    enqueueSnackbar(strings('insufficientStock'), {
      variant: 'error',
    });
  };

  const { quantity: itemInCartQty } =
    itemType === 'products' ? products[idx] : services[idx];
  let allInCartQtySum = 0;
  const productsInCart = products.filter((p) => p.id === item.id);

  if (productsInCart.length > 0) {
    allInCartQtySum = productsInCart
      .map((product) => product.quantity)
      .reduce((curr, next) => curr + next);
  }

  const handleNewPrice = (price: string) => {
    dispatch(
      updatePrice({
        idx,
        price,
        transactionType,
        itemType,
      })
    );
  };

  const handleQuantityChange = (quantity: number) => {
    if (itemType === 'products') {
      const { stock, stock_unit } = itemAsProduct;
      const q = roundPosSaleQuantity(quantity, stock_unit);
      const minQ = minPosSaleQuantity(stock_unit);
      if (q < minQ) {
        return;
      }

      const newTotalInCart = projectedCartQtyForProductLine(
        products,
        idx,
        q
      );
      if (newTotalInCart === null) return;

      const maxAvailable = stock + (originalQtyInSale ?? 0);
      if (newTotalInCart > maxAvailable) {
        showInvalidStockMessage();
        return;
      }

      dispatch(
        updateQuantity({
          idx,
          quantity: q,
          transactionType,
          itemType,
        })
      );
      return;
    }

    dispatch(
      updateQuantity({
        idx,
        quantity,
        transactionType,
        itemType,
      })
    );
  };

  const handleQuantityIncrease = () => {
    if (itemType === 'products') {
      const step = posSaleQuantityStep(itemAsProduct.stock_unit);
      handleQuantityChange(
        roundPosSaleQuantity(
          item.quantity + step,
          itemAsProduct.stock_unit
        )
      );
      return;
    }
    handleQuantityChange(item.quantity + 1);
  };

  const handleQuantityDecrease = () => {
    if (itemType === 'products') {
      const step = posSaleQuantityStep(itemAsProduct.stock_unit);
      const minQ = minPosSaleQuantity(itemAsProduct.stock_unit);
      const nextQty = Math.max(
        minQ,
        roundPosSaleQuantity(
          item.quantity - step,
          itemAsProduct.stock_unit
        )
      );
      dispatch(
        updateQuantity({
          idx,
          quantity: nextQty,
          transactionType,
          itemType,
        })
      );
      return;
    }
    dispatch(
      updateQuantity({
        idx,
        quantity: item.quantity - 1,
        transactionType,
        itemType,
      })
    );
  };

  const onMaxReached = () => {
    showInvalidStockMessage();
  };

  let max: number | undefined;

  if (itemType === 'products') {
    max = itemAsProduct.stock + itemInCartQty - allInCartQtySum;

    if (originalQtyInSale) {
      max =
        itemAsProduct.stock +
        originalQtyInSale +
        itemInCartQty -
        allInCartQtySum;
    }
  }

  return (
    <div style={{ marginBottom: 10 }}>
      <Stack spacing={1} marginBottom={2} direction="row" alignItems="center">
        <IconButton size="small" color="error" onClick={() => removeItem()}>
          <Delete fontSize="small" />
        </IconButton>
        <Tooltip title={item.name}>
          <Typography noWrap fontSize={18} fontWeight="bold" marginBottom={1}>
            {item.name}
          </Typography>
        </Tooltip>
      </Stack>
      <Grid container spacing={2} alignItems="center">
        {!disablePriceField && (
          <Grid item xs={12} sm={6}>
            {itemType === 'products' ? (
              <TextField
                type="number"
                label={strings('salePrice')}
                value={item.price}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={strings('priceTooltip')}>
                        <IconButton>
                          <Help />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                onKeyDown={(e) =>
                  exceptThisSymbols.includes(e.key) && e.preventDefault()
                }
                onChange={(e) => handleNewPrice(e.target.value)}
              />
            ) : (
              <Typography variant="body1">
                {strings('price')}: {fCurrency(item.price)}
              </Typography>
            )}
          </Grid>
        )}
        <Grid item xs={12} sm={6}>
          <IncrementerButton
            value={item.quantity}
            max={itemType === 'products' ? max : undefined}
            onMaxReached={onMaxReached}
            allowDecimalQuantity={
              itemType === 'products' &&
              !isDiscreteProductStockUnit(itemAsProduct.stock_unit)
            }
            onQtyChange={(quantity) => handleQuantityChange(quantity)}
            disabledDecrease={
              itemType === 'products'
                ? item.quantity <= minPosSaleQuantity(itemAsProduct.stock_unit)
                : item.quantity <= 1
            }
            onIncrease={handleQuantityIncrease}
            onDecrease={handleQuantityDecrease}
          />
        </Grid>
      </Grid>
    </div>
  );
};
